// A section's meeting times (#609), shared by the create wizard, the
// detail page and the summary

// What a meeting is (#609), e.g. a section's lecture and its lab
export const MEETING_TYPE_OPTIONS = [
    { value: 'lecture', label: 'Lecture' },
    { value: 'lab', label: 'Lab' },
    { value: 'recitation', label: 'Recitation' },
    { value: 'other', label: 'Other' },
];

// The weekly LA meeting lengths a request can state, in hours (30 minutes
// to 4 hours), shared by the create wizard and the coordinator editor
export const MEETING_HOUR_OPTIONS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4].map(
    (h) => ({
        value: h,
        label: h === 0.5 ? '30 minutes' : `${h} ${h === 1 ? 'hour' : 'hours'}`,
    })
);

// The server refuses more (application.controller.js's MAX_MEETINGS)
export const MAX_MEETINGS = 10;

export const emptyMeeting = () => ({
    type: '',
    otherType: '',
    days: '',
    otherDays: '',
    time: '',
    otherTime: '',
    room: '',
});

// A saved courseConfirmation's meetings in the form's shape. One saved
// before #609 has a single schedule instead, which becomes the first
// meeting (with no type, since that wasn't asked yet).
export function meetingsFromSaved(courseConfirmation) {
    const saved = courseConfirmation?.meetings?.length
        ? courseConfirmation.meetings
        : [courseConfirmation?.schedule || {}];
    // Every field emptyMeeting has, so a new one can't be dropped here
    const fields = Object.keys(emptyMeeting());
    return saved.map((m) =>
        Object.fromEntries(fields.map((field) => [field, m[field] || '']))
    );
}

// The same kinds of meetings (lecture and lab) with their days, time and
// room cleared, for a duplicate (#457), since each section meets at its
// own times
export const meetingTypesOnly = (meetings) =>
    meetings.map(({ type, otherType }) => ({
        ...emptyMeeting(),
        type,
        otherType,
    }));

// Meetings are only numbered when there's more than one
export const meetingNumber = (meetings, index) =>
    meetings.length > 1 ? index + 1 : null;

// e.g. "Days" or "Meeting 2 days", and "Meeting type" or "Meeting 2 type"
export function meetingLabel(meetings, index, label) {
    const number = meetingNumber(meetings, index);
    if (!number) return label;
    const field = label.replace(/^meeting /i, '').toLowerCase();
    return `Meeting ${number} ${field}`;
}

// A meeting's selects that have an "Other" choice, which then needs a
// description, in the order they render
const SELECTS_WITH_OTHER = [
    {
        field: 'type',
        other: 'other',
        text: 'otherType',
        label: 'Meeting type',
        textLabel: 'Meeting type description',
    },
    {
        field: 'days',
        other: 'Other',
        text: 'otherDays',
        label: 'Days',
        textLabel: 'Day pattern description',
    },
    {
        field: 'time',
        other: 'Other',
        text: 'otherTime',
        label: 'Time',
        textLabel: 'Time description',
    },
];

// Every meeting's missing answers, in the order they render, with ids
// `${idPrefix}-meeting-${i}-<field>`. With required off, only an "Other"
// choice missing its description counts. firstUntyped lets the first meeting
// go without a type, for a request saved before the type was asked.
export function getMeetingErrors(
    meetings,
    idPrefix,
    { required = true, firstUntyped = false } = {}
) {
    return meetings.flatMap((meeting, i) => {
        const id = `${idPrefix}-meeting-${i}`;
        const label = (text) => meetingLabel(meetings, i, text);
        const errors = [];
        for (const select of SELECTS_WITH_OTHER) {
            const optional = firstUntyped && i === 0 && select.field === 'type';
            if (required && !optional && !meeting[select.field]) {
                errors.push({
                    id: `${id}-${select.field}`,
                    label: label(select.label),
                });
            }
            if (
                meeting[select.field] === select.other &&
                !meeting[select.text]?.trim()
            ) {
                errors.push({
                    id: `${id}-other-${select.field}`,
                    label: label(select.textLabel),
                });
            }
        }
        if (required && !meeting.room?.trim()) {
            errors.push({ id: `${id}-room`, label: label('Room') });
        }
        return errors;
    });
}
